import React from 'react';
import { useSAData } from './useSAData';
import { SAKpis } from './SAKpis';
import { Loading } from './Loading';
import { BarList, ChartCard } from './SACharts';
import { AnalyticsTable } from '@/components/po-grn/AnalyticsTable';
import { GROUP_COLS, PRODUCT_COLS } from './columns';
import { F } from './formulas';
import { inr, pct } from '@/components/po-grn/format';
import { Bar, BarChart, CartesianGrid, Cell, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';

const KPIS = [
  { key: 'distinct_skus', label: 'Distinct SKUs Sold' },
  { key: 'sales', label: 'Total Sales (Incl. GST)', type: 'money', drill: { patch: { flag: ['sales'] } } },
  { key: 'qty_by_unit', label: 'Units Sold (by unit)', type: 'units' },
  { key: 'margin_pct', label: 'Current Margin %', type: 'pct', note: F.current },
  { key: 'prm_pct', label: 'Outward Margin %', type: 'pct', note: F.outward },
  { key: 'im_pct', label: 'Inward Margin %', type: 'pct', note: F.inward },
  { key: 'neg_products', label: 'Products with Negative Current Margin', warn: true, drill: { patch: { flag: ['sales', 'negative_margin'] } } },
  { key: 'pricing_issue_skus', label: 'SKUs with Pricing Inconsistencies', warn: true, drill: { patch: { flag: ['sales', 'missing_pricing'] } } },
];

const List = ({ title, rows, valueKey, fmt, onPick, testId, note }) => (
  <div className="rounded-xl border border-orange-200/70 bg-white p-3" data-testid={testId}>
    <div className="text-xs font-semibold text-[#3b2416]">{title}</div>
    {note && <div className="text-[10px] text-stone-500 mb-1">{note}</div>}
    {(rows || []).map((g, i) => (
      <button key={g.key} type="button" onClick={() => onPick(g)} className="w-full flex justify-between gap-2 text-[11px] py-1 px-1 rounded hover:bg-orange-50" data-testid={`${testId}-item`}>
        <span className="truncate text-stone-700"><span className="text-stone-400 mr-1">{i + 1}.</span>{g.key} · {g.name}</span>
        <span className={`tabular-nums font-semibold ${g[valueKey] < 0 ? 'text-red-600' : 'text-[#3b2416]'}`}>{fmt(g[valueKey])}</span>
      </button>
    ))}
    {!rows?.length && <div className="text-[11px] text-stone-400 py-3">No products</div>}
  </div>
);

export const ProductsTab = ({ params, onDrill }) => {
  const { data, loading } = useSAData('/products', params);
  const pick = (g) => onDrill({ patch: { skus: [g.key || g.product_id], flag: ['sales'] } }, `Product ${g.key || g.product_id}`);
  return (
    <div className="space-y-4 relative">
      <Loading on={loading} />
      <SAKpis items={KPIS} data={data?.kpis} onDrill={onDrill} testPrefix="sa-products" />
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-5 gap-3">
        <List title="Top-Selling Products" rows={data?.top_selling} valueKey="sales" fmt={inr} onPick={pick} testId="sa-prod-top" />
        <List title="Highest Current Margin %" note={F.current} rows={data?.highest_margin} valueKey="margin_pct" fmt={pct} onPick={pick} testId="sa-prod-high-margin" />
        <List title="Lowest Current Margin %" note={F.current} rows={data?.lowest_margin} valueKey="margin_pct" fmt={pct} onPick={pick} testId="sa-prod-low-margin" />
        <List title="Highest Outward Margin %" note={F.outward} rows={data?.highest_outward} valueKey="prm_pct" fmt={pct} onPick={pick} testId="sa-prod-high-outward" />
        <List title="Lowest Outward Margin %" note={F.outward} rows={data?.lowest_outward} valueKey="prm_pct" fmt={pct} onPick={pick} testId="sa-prod-low-outward" />
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <ChartCard title="Products by Current Margin %" subtitle={F.current} testId="sa-prod-margin-dist">
          <ResponsiveContainer>
            <BarChart data={data?.margin_distribution || []}>
              <CartesianGrid strokeDasharray="3 3" stroke="#fed7aa" vertical={false} />
              <XAxis dataKey="bucket" fontSize={10} /><YAxis fontSize={10} allowDecimals={false} /><Tooltip />
              <Bar dataKey="products" name="Products" radius={[3, 3, 0, 0]}>
                {(data?.margin_distribution || []).map((d) => <Cell key={d.bucket} fill={d.bucket === '< 0%' ? '#dc2626' : '#ea580c'} />)}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>
        <BarList title="Sales by Category" data={data?.by_category || []} testId="sa-prod-by-category"
          onPick={(c) => c.key !== 'Not in master' && onDrill({ patch: { categories: [c.key], flag: ['sales'] } }, `Category ${c.key}`)} />
        <BarList title="Sales by Brand" data={data?.by_brand || []} testId="sa-prod-by-brand"
          onPick={(c) => c.key !== 'Not in master' && onDrill({ patch: { brands: [c.key], flag: ['sales'] } }, `Brand ${c.key}`)} />
      </div>
      <div className="rounded-xl border border-orange-200/70 bg-white p-3" data-testid="sa-prod-bought-together">
        <div className="text-xs font-semibold text-[#3b2416] mb-2">Products Commonly Bought Together (same invoice, ≥ 2 invoices)</div>
        {!data?.bought_together?.length && <div className="text-[11px] text-stone-400">Not enough transaction data yet</div>}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
          {(data?.bought_together || []).map((p) => (
            <div key={`${p.a}-${p.b}`} className="text-[11px] rounded border border-orange-100 px-2 py-1 text-stone-700">
              {p.a_name} + {p.b_name} <span className="float-right font-semibold text-[#3b2416]">{p.invoices} inv.</span>
            </div>
          ))}
        </div>
      </div>
      <AnalyticsTable columns={GROUP_COLS('Category')} rows={data?.by_category || []} testId="sa-prod-category-table" rowKey={(r) => r.key} />
      <AnalyticsTable columns={PRODUCT_COLS} rows={data?.table || []} testId="sa-product-table" rowKey={(r) => r.row_key} />
    </div>
  );
};
